import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { StorageContext } from '../contexts/StorageContext';
import LoadingSpinner from '../components/LoadingSpinner';
import './ProjectSettings.css';

/**
 * Project settings page component
 */
function ProjectSettings() {
  const { projectId } = useParams();
  const { storage } = useContext(StorageContext);
  const [project, setProject] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Load project data
  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const projectData = await storage.getProject(projectId);
        
        if (!projectData) {
          setError('Project not found');
          return;
        }
        
        setProject(projectData);
        setProjectName(projectData.name);
        setProjectDescription(projectData.description || '');
      } catch (err) {
        console.error('Error loading project:', err);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    
    loadProject();
  }, [projectId, storage]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const updatedProject = {
        ...project,
        name: projectName.trim(),
        description: projectDescription.trim(),
        updated: Date.now()
      };
      
      await storage.updateProject(updatedProject);
      
      // Navigate back to project
      navigate(`/project/${projectId}`);
    } catch (err) {
      console.error('Error saving project:', err);
      setError('Failed to save project settings');
    } finally {
      setSaving(false);
    }
  };

  // Handle project deletion
  const handleDelete = async () => {
    const confirm = window.confirm(
      'Are you sure you want to delete this project? This action cannot be undone.'
    );
    
    if (!confirm) return;
    
    try {
      setLoading(true);
      
      await storage.deleteProject(projectId);
      
      // Redirect to home page
      navigate('/');
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to delete project');
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <LoadingSpinner message="Loading project settings..." />
      </div>
    );
  }

  // Show error state
  if (error && !project) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-danger)' }}>
        <h2>Error</h2>
        <p>{error}</p>
        <Link to={`/project/${projectId}`} style={{ display: 'inline-block', marginTop: '1rem' }}>
          Return to Project
        </Link>
      </div>
    );
  }

  return (
    <div className="project-settings-page">
      <div className="settings-header">
        <h1>Project Settings</h1>
        <Link to={`/project/${projectId}`} className="back-link">
          &larr; Back to Project
        </Link>
      </div>
      
      {error && (
        <div className="settings-error">
          {error}
        </div>
      )}
      
      <div className="settings-form-container">
        <form onSubmit={handleSubmit} className="settings-form">
          <div className="form-group">
            <label htmlFor="project-name">Project Name</label>
            <input
              type="text"
              id="project-name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="project-description">Description (Optional)</label>
            <textarea
              id="project-description"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              rows="4"
            />
          </div>
          
          <div className="form-actions">
            <button
              type="submit"
              className="save-button"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
      
      <div className="danger-zone">
        <h2>Danger Zone</h2>
        <p>
          Once you delete a project, there is no going back. Please be certain.
        </p>
        <button
          type="button"
          className="delete-button"
          onClick={handleDelete}
          disabled={loading}
        >
          Delete Project
        </button>
      </div>
    </div>
  );
}

export default ProjectSettings;